import { getVariableHtml, StaticVariableTag, TicketingFormCategory } from "@simplyk/common";

import { GetCurrentUserFrontendObject } from "@/common/contexts/types";
import { Translate } from "@/common/hooks/useTranslate";
import { OrganizationOutput } from "@/common/types/trpc";

interface TicketingCategoryDefaults {
  emailObject: string;
  emailContent: string;
}

export interface TicketingEmailTemplates {
  emailObject: string;
  emailContent: string;
}

interface GetTicketingThankYouEmailTemplatesParams {
  category: TicketingFormCategory;
  t: Translate;
  organization: OrganizationOutput | null | undefined;
  currentUser: GetCurrentUserFrontendObject | null | undefined;
}

/**
 * Generates default thank you email templates for ticketing forms based on category.
 * This is used both for initial form creation and as fallback when AI generation fails.
 */
export const getTicketingThankYouEmailTemplates = ({
  category,
  t,
  organization,
  currentUser,
}: GetTicketingThankYouEmailTemplatesParams): TicketingEmailTemplates => {
  const TICKETING_CATEGORY_DEFAULTS: Record<TicketingFormCategory, TicketingCategoryDefaults> = {
    [TicketingFormCategory.Event]: {
      emailObject: t("common", "thankYouForYourSupport"),
      emailContent: t("dashboard", "ticketing.defaultFormEmailBody2"),
    },
    [TicketingFormCategory.Shop]: {
      emailObject: t("common", "thankYouForYourSupport"),
      emailContent: t("dashboard", "ticketing.defaultFormEmailBody2.shop"),
    },
    [TicketingFormCategory.Auction]: {
      emailObject: t("dashboard", "ticketing.defaultFormEmailObject.auction.winner", {
        organizationName: organization?.name || "",
      }),
      emailContent: t("dashboard", "ticketing.defaultFormEmailBody2.auction.winner", {
        organizationName: organization?.name || "",
      }),
    },
    [TicketingFormCategory.Lottery]: {
      emailObject: t("common", "thankYouForYourSupport"),
      emailContent: t("dashboard", "ticketing.defaultFormEmailBody2.lottery"),
    },
    [TicketingFormCategory.Sweepstakes]: {
      emailObject: t("dashboard", "sweepstakes.email.subject"),
      emailContent: t("dashboard", "sweepstakes.email.body"),
    },
    [TicketingFormCategory.MembershipV2]: {
      emailObject: t("common", "thankYouForYourSupport"),
      emailContent: t("dashboard", "ticketing.defaultFormEmailBody2.membership"),
    },
    [TicketingFormCategory.Custom]: {
      emailObject: t("common", "thankYouForYourSupport"),
      emailContent: t("dashboard", "ticketing.defaultFormEmailBody2.shop"),
    },
  };

  const defaults = TICKETING_CATEGORY_DEFAULTS[category];

  const greetsRecipientByName = category !== TicketingFormCategory.MembershipV2;

  const buildEmailBody = (emailBody: string) =>
    (greetsRecipientByName
      ? t("dashboard", "ticketing.defaultFormEmailBody1") +
        getVariableHtml({ text: t("dashboard", "common.firstName"), tag: StaticVariableTag.FirstName })
      : t("dashboard", "ticketing.defaultFormEmailBody1.noName")) +
    emailBody +
    t("dashboard", "ticketing.defaultFormEmailBody3", {
      firstName: currentUser?.firstName || "",
      lastName: currentUser?.lastName || "",
      email: currentUser?.email || "",
    });

  return {
    emailObject: defaults.emailObject,
    emailContent: buildEmailBody(defaults.emailContent),
  };
};
